import { useEffect } from 'react';
import { useApp } from '@/lib/core/providers/appProvider';
import { formatDate, formatTime, getFirstName, ImagesToImageMap } from '@/lib/core/utils';
import type { ConfirmationProps } from '../types';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { ConfirmationBanner } from '@/components/shared/booking/common/confirmation/banner';

export function RangeRoverConfirmation({
  booking,
  cmsProps,
  serviceCategory,
  onGoHome,
  onSeeRideDetails,
  ride,
}: ConfirmationProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(s => s.locale);
  const { user } = useApp() as { user?: { name?: string | null } };
  const setMainClassName = useUIStore(s => s.setMainClassName);

  useEffect(() => {
    setMainClassName('pb-0!');
    return () => setMainClassName('');
  }, [setMainClassName]);

  const firstName = getFirstName(user?.name) ?? null;
  const bannerImage = ImagesToImageMap(serviceCategory?.properties?.images, 'bookingDetailHero');

  const pickupDisplay = booking.pickupPoint?.name ?? '';
  const dropoffDisplay = booking.dropoffPoint?.name ?? '';
  const dateDisplay = booking.date ? formatDate(booking.date?.toDateString(), locale) : '';
  const timeDisplay = booking.time ? formatTime(booking.time) : '';

  const titleDisplay = ride?.status == 'SCHEDULED' ? cmsProps?.scheduledTitle : cmsProps?.title;
  const detailsLabel =
    ride?.status == 'SCHEDULED' ? cmsProps?.seeBookingDetailsLabel : cmsProps?.seeRideDetailsLabel;

  return (
    <ConfirmationBanner
      image={bannerImage}
      firstName={firstName}
      title={titleDisplay ?? t('App.Labels.BookingConfirmedText')}
      rows={[
        [
          ...(dateDisplay ? [{ text: dateDisplay }] : []),
          ...(timeDisplay ? [{ icon: 'clock', text: timeDisplay }] : []),
        ],
        [...(pickupDisplay ? [{ icon: 'map-pin', text: pickupDisplay }] : [])],
        [...(dropoffDisplay ? [{ icon: 'map-pin', text: dropoffDisplay }] : [])],
      ]}
      actions={[
        {
          label: cmsProps?.backToHomeLabel ?? t('App.Labels.GoBackHome'),
          onClick: onGoHome,
          variant: 'secondary-outline',
        },
        {
          label: detailsLabel ?? t('App.Labels.SeeBookingDetails'),
          onClick: onSeeRideDetails,
          variant: 'primary-solid',
        },
      ]}
      className="-mt-[max(1rem,env(safe-area-inset-top))]!"
      contentClassName="justify-between pt-[calc(112px+117px)]!"
    />
  );
}
